import { useState } from 'react';

import { useAppDispatch } from '@verantos-dev/app-state-base';
import { setConceptGroups } from '@verantos-dev/app-state-cohort-builder';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  useConceptGroups,
  usePostCreateConceptGroupMutation,
  usePostConfirmPreviewMutation,
} from '@verantos-dev/data-access-concept-groups';
import { IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

export const useCreateConceptGroup = (
  conceptGroupName: string,
  concepts?: IsValidCreateUploadResponse[],
  projectId?: string,
  onSuccessCallback?: (conceptGroupId: string, filterKey?: string) => void,
  filterKey?: string,
  previewId?: string
) => {
  const dispatch = useAppDispatch();
  const { refetchConceptGroups } = useConceptGroups();
  const [mutate, { error, data }] = usePostCreateConceptGroupMutation();
  const [confirmMutate, { error: confirmError, data: confirmData }] =
    usePostConfirmPreviewMutation();

  // This is handle the lag between create and refetch
  const [isCreatingAndFetching, setIsCreatingAndFetching] = useState(false);

  const create = async () => {
    try {
      setIsCreatingAndFetching(true);

      // Preview mode: confirm via REST instead of sending full concept list
      if (previewId) {
        const result = await confirmMutate({
          previewId,
          conceptGroupName,
          projectId,
          filterKey,
        }).unwrap();

        if (onSuccessCallback && result?.data?.conceptGroupId) {
          const { data } = await refetchConceptGroups();
          const conceptGroups = data?.data;
          dispatch(setConceptGroups(conceptGroups));

          await new Promise((resolve) => setTimeout(resolve, 100));

          const returnedFilterKey = result.data.filterKey || filterKey;
          onSuccessCallback(result.data.conceptGroupId, returnedFilterKey);
        }

        setIsCreatingAndFetching(false);
        return result;
      }

      // Legacy mode: send full concept IDs
      if (conceptGroupName && concepts && concepts.length > 0) {
        const result = await mutate({
          conceptGroupName,
          conceptIds: concepts.filter((c) => c.isValid).map((c) => String(c.conceptId)),
          projectId,
          filterKey,
        }).unwrap();

        if (onSuccessCallback && result?.data?.conceptGroupId) {
          const { data } = await refetchConceptGroups();
          const conceptGroups = data?.data;
          dispatch(setConceptGroups(conceptGroups));

          await new Promise((resolve) => setTimeout(resolve, 100));

          const returnedFilterKey = result.data.filterKey || filterKey;
          onSuccessCallback(result.data.conceptGroupId, returnedFilterKey);
        }

        setIsCreatingAndFetching(false);
        return result;
      }

      setIsCreatingAndFetching(false);
      return null;
    } catch (err) {
      console.error(err);
      setIsCreatingAndFetching(false);
      return null;
    }
  };

  const activeError = previewId ? confirmError : error;
  const activeData = previewId ? confirmData : data;

  return {
    create,
    isCreating: isCreatingAndFetching,
    creationError: activeError,
    isSuccess: Boolean(activeData && !activeError && !isCreatingAndFetching),
  };
};
